import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

// Module-level state — shared across all concurrent interceptor invocations
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function isPublicAuthUrl(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password')
  );
}

function addAuthHeader(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function handle401(
  authService: AuthService,
  req: HttpRequest<unknown>,
  next: Parameters<HttpInterceptorFn>[1],
  originalError: HttpErrorResponse,
): Observable<HttpEvent<unknown>> {
  if (isRefreshing) {
    // Another refresh is already in flight — queue this request until it finishes
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(addAuthHeader(req, token!))),
    );
  }

  isRefreshing = true;
  refreshTokenSubject.next(null); // block queued requests

  return authService.refreshToken().pipe(
    switchMap((response) => {
      const newToken = response.data?.accessToken;
      isRefreshing = false;
      if (!newToken) {
        refreshTokenSubject.next(null);
        authService.logout();
        return throwError(() => originalError);
      }
      refreshTokenSubject.next(newToken); // unblock all queued requests
      return next(addAuthHeader(req, newToken));
    }),
    catchError((refreshError) => {
      isRefreshing = false;
      refreshTokenSubject.next(null);
      authService.logout();
      return throwError(() => refreshError);
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getAccessToken();

  const url = req.url.toLowerCase();
  const isPublic = isPublicAuthUrl(url);

  const authReq = token && !isPublic ? addAuthHeader(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !url.includes('/auth/refresh') && !isPublic) {
        return handle401(authService, req, next, error);
      }
      return throwError(() => error);
    }),
  );
};
